import { computed } from 'vue'

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'

import { NotificationService } from '../services/notification.service'
import { notificationQueryKeys } from './notification.query-keys'

import type { INotification } from '../types/notification.types'

export const useNotificationQueryStore = () => {
  const queryClient = useQueryClient()

  // ===== QUERY KEY FACTORY =====
  const queryKeys = notificationQueryKeys

  // ===== QUERY CREATION FUNCTIONS =====

  /**
   * Infinite query for fetching all notifications with pagination
   */
  const createAllNotificationsInfiniteQuery = () => {
    const infiniteQuery = useInfiniteQuery<INotification[], Error>({
      queryKey: queryKeys.infinite.all(),
      initialPageParam: undefined as string | undefined,
      queryFn: async ({ pageParam }) => {
        const service = new NotificationService()
        const response = await service.getNotifications({
          previousId: pageParam as string | undefined,
          limit: 20,
          pageSort: 'descending',
        })
        return response.data || []
      },
      getNextPageParam: lastPage => {
        // Return the last notification ID for cursor-based pagination
        if (lastPage && lastPage.length > 0) {
          return lastPage[lastPage.length - 1]._id
        }
        return undefined
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: true,
    })

    // Add computed allData property that flattens all pages
    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  /**
   * Infinite query for fetching only unread notifications with pagination
   */
  const createUnreadNotificationsInfiniteQuery = () => {
    const infiniteQuery = useInfiniteQuery<INotification[], Error>({
      queryKey: queryKeys.infinite.unread(),
      initialPageParam: undefined as string | undefined,
      queryFn: async ({ pageParam }) => {
        const service = new NotificationService()
        const response = await service.getNotifications({
          isRead: false, // Hardcoded for unread notifications
          previousId: pageParam as string | undefined,
          limit: 20,
          pageSort: 'descending',
        })
        return response.data || []
      },
      getNextPageParam: lastPage => {
        // Return the last notification ID for cursor-based pagination
        if (lastPage && lastPage.length > 0) {
          return lastPage[lastPage.length - 1]._id
        }
        return undefined
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: true,
    })

    // Add computed allData property that flattens all pages
    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // ===== MUTATIONS =====

  const createMarkAsReadMutation = useMutation({
    mutationFn: async ({ notificationId, refId }: { notificationId?: string; refId?: string }) => {
      const service = new NotificationService()
      return service.markNotificationAsRead(notificationId, refId)
    },
    onSuccess: () => {
      // Invalidate all infinite queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.infinite.all(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.infinite.unread(),
      })
    },
  })

  // ===== HELPER FUNCTIONS =====

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.all(),
    })
  }

  const clearAll = () => {
    queryClient.removeQueries({
      queryKey: queryKeys.all(),
    })
  }

  return {
    // Query creation functions
    createAllNotificationsInfiniteQuery,
    createUnreadNotificationsInfiniteQuery,

    // Mutations
    createMarkAsReadMutation,

    // Helper functions
    invalidateAll,
    clearAll,

    // Query keys for manual invalidation
    queryKeys,
  }
}
